import type { Database, Customer, Request, Property, Job, JobStatus, RequestStatus, UrgencyLevel, PropertyType, DayOfWeek, Quote, QuoteStatus, Service, ServiceCategory } from '@cleanpro/common-ui';
import { QUOTE_STATUS } from '@cleanpro/common-ui';
import type { Business, BusinessAddress, BusinessAvailability } from '../service-providers/business';

type CustomerRow = Database['public']['Tables']['customers']['Row'];
type RequestRow = Database['public']['Tables']['requests']['Row'];
type PropertyRow = Database['public']['Tables']['properties']['Row'];
type BusinessRow = Database['public']['Tables']['businesses']['Row'];
type JobRow = Database['public']['Tables']['jobs']['Row'];
type QuoteRow = Database['public']['Tables']['quotes']['Row'];
type ServiceRow = Database['public']['Tables']['services']['Row'];

/**
 * Transforms a database quote row to the Quote interface
 * This is a reusable transformer that can be used across the application
 * when working with quote data from Supabase queries.
 */
export const transformQuote = (row: QuoteRow): Quote => ({
  id: row.id,
  acceptedAt: row.accepted_at || undefined,
  cancelledAt: row.cancelled_at || undefined,
  createdAt: row.created_at || '',
  customerId: row.customer_id || '',
  customerNotes: row.customer_notes || undefined,
  discountAmount: row.discount_amount || 0,
  internalNotes: row.internal_notes || undefined,
  lineItems: (() => {
    if (typeof row.line_items === 'string') return JSON.parse(row.line_items);
    if (Array.isArray(row.line_items)) return row.line_items;
    return [];
  })(),
  quoteNumber: row.quote_number,
  rejectedAt: row.rejected_at || undefined,
  requestId: row.request_id || undefined,
  requestedDateTime: row.requested_date_time || undefined,
  requiredDeposit: row.required_deposit || 0,
  respondedAt: row.responded_at || undefined,
  sentAt: row.sent_at || undefined,
  status: (row.status as QuoteStatus) || QUOTE_STATUS.DRAFT,
  subtotal: row.subtotal || 0,
  taxAmount: row.tax_amount || 0,
  taxRate: row.tax_rate || 0,
  terms: row.terms || undefined,
  totalAmount: row.total_amount || 0,
  updatedAt: row.updated_at || '',
  validUntil: row.valid_until,
  viewedAt: row.viewed_at || undefined,
  isExclusive: row.is_exclusive || undefined,
  exclusiveExpiredAt: row.exclusive_expired_at || undefined,
});

/**
 * Transforms a database customer row to the Customer interface
 * This is a reusable transformer that can be used across the application
 * when working with customer data from Supabase queries.
 */
export const transformCustomer = (row: CustomerRow | null | undefined): Customer | undefined => {
  if (!row) return undefined;
  
  return {
    id: row.id,
    name: row.name,
    email: row.email,
    phone: row.phone,
    address: undefined, // customers table doesn't have a single address field
    rating: row.rating || undefined,
    totalSpent: row.total_spent || undefined,
    lastService: row.last_service || undefined,
    createdAt: row.created_at || undefined,
    updatedAt: row.updated_at || undefined,
  };
};

/**
 * Transforms a database request row to the Request interface
 * This is a reusable transformer that can be used across the application
 * when working with request data from Supabase queries.
 */
export const transformRequest = (row: RequestRow | null | undefined): Request | undefined => {
  if (!row) return undefined;
  
  return {
    id: row.id,
    customerId: row.customer_id || '',
    serviceId: row.service_id,
    propertyId: row.property_id || '',
    status: row.status as RequestStatus,
    requestedDateTime: row.requested_date_time || undefined,
    urgency: row.urgency as UrgencyLevel,
    estimatedDuration: row.estimated_duration || undefined,
    accessInstructions: row.access_instructions || undefined,
    referral: row.referral || undefined,
    notes: row.notes || undefined,
    exclusiveBusinessId: row.exclusive_business_id || undefined,
    exclusiveUntil: row.exclusive_until || undefined,
    createdAt: row.created_at || '',
    updatedAt: row.updated_at || '',
  };
};

/**
 * Transforms a database property row to the Property interface
 * This is a reusable transformer that can be used across the application
 * when working with property data from Supabase queries.
 */
export const transformProperty = (row: PropertyRow | null | undefined): Property | undefined => {
  if (!row) return undefined;
  
  return {
    id: row.id,
    name: row.name,
    customerId: row.customer_id,
    propertyType: row.property_type as PropertyType,
    addressLine1: row.address_line_1,
    addressLine2: row.address_line_2 || undefined,
    city: row.city,
    state: row.state,
    postalCode: row.postal_code || '',
    squareFootage: row.square_footage || undefined,
    bedrooms: row.bedrooms || 0,
    bathrooms: row.bathrooms || 0,
    floors: row.floors || 1,
    accessInstructions: row.access_instructions || undefined,
    specialInstructions: row.special_instructions || undefined,
    hasPets: row.has_pets || false,
    petDetails: row.pet_details || undefined,
    preferredCleaningDay: row.preferred_cleaning_day as DayOfWeek | undefined,
    requestedTimeSlot: row.requested_time_slot as 'morning' | 'midday' | 'afternoon' | undefined,
    isActive: row.is_active ?? true,
    createdAt: row.created_at || '',
    updatedAt: row.updated_at || '',
  };
};

/**
 * Transforms a database business row to the Business interface
 * This is a reusable transformer that can be used across the application
 * when working with business data from Supabase queries.
 */
export const transformBusiness = (row: BusinessRow | null | undefined): Business | undefined => {
  if (!row) return undefined;
  
  // Parse availability if stored as JSON (checking actual DB field names)
  const availability: BusinessAvailability = {
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  };
  
  // Build address object
  const address: BusinessAddress = {
    addressLine1: row.address_line_1 || '',
    addressLine2: row.address_line_2 || '',
    addressCity: row.city || '',
    addressState: row.state || '',
    addressPostalCode: row.postal_code || '',
  };
  
  return {
    id: row.id,
    businessName: row.business_name,
    ownerName: row.owner_name || '',
    email: row.email,
    phone: row.phone,
    address,
    businessType: row.business_type as 'independent_contractor',
    status: row.status as 'active' | 'inactive' | 'pending',
    contractStartDate: row.contract_start_date || '',
    servicesOffered: typeof row.services_offered === 'string' 
      ? JSON.parse(row.services_offered) 
      : (row.services_offered || []),
    hourlyRate: row.hourly_rate || 0,
    availability,
    rating: row.rating || 0,
    completedJobs: row.completed_jobs || 0,
    insuranceInfo: row.insurance_provider ? {
      provider: row.insurance_provider,
      policyNumber: row.insurance_policy_number || '',
      expirationDate: row.insurance_expiration_date || '',
    } : undefined,
    userId: row.user_id || undefined,
    servicePostalCodes: typeof row.service_postal_codes === 'string'
      ? JSON.parse(row.service_postal_codes)
      : (row.service_postal_codes || []),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
};

/**
 * Transforms a database job row to the Job interface
 * This is a reusable transformer that can be used across the application
 * when working with job data from Supabase queries.
 */
export const transformJob = (row: JobRow | null | undefined): Job | undefined => {
  if (!row) return undefined;
  
  return {
    id: row.id,
    status: row.status as JobStatus,
    price: row.price,
    notes: row.notes || '',
    date_time: row.date_time,
    duration: row.duration,
    isRecurring: row.is_recurring || false,
    recurringFrequency: row.recurring_frequency as 'weekly' | 'bi-weekly' | 'tri-weekly' | 'monthly' | undefined,
    recurringEndDate: row.recurring_end_date || undefined,
    recurringParentId: row.recurring_parent_id || undefined,
    createdAt: row.created_at || undefined,
    updatedAt: row.updated_at || undefined,
  };
};

/**
 * Transforms a database service row to the Service interface
 * This is a reusable transformer that can be used across the application
 * when working with service data from Supabase queries.
 */
export const transformService = (row: ServiceRow | null | undefined): Service | undefined => {
  if (!row) return undefined;
  
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    basePrice: row.base_price,
    duration: row.duration,
    category: row.category as ServiceCategory,
    addOns: typeof row.add_ons === 'string' 
      ? JSON.parse(row.add_ons) 
      : (row.add_ons || []),
    isActive: undefined, // Database doesn't have is_active field
    createdAt: row.created_at || '',
    updatedAt: row.updated_at || '',
  };
};
